import {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  OAuthLoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  User,
} from '../types/auth';
import { BaseApiService } from './baseApiService';

/**
 * Service for authentication and user profile operations
 */
class AuthService extends BaseApiService {
  async login(credentials: LoginRequest) {
    await this.ensureInitialized();
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest) {
    await this.ensureInitialized();
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async oauthLogin(oauthData: OAuthLoginRequest) {
    await this.ensureInitialized();
    const response = await this.api.post('/auth/oauth/login', oauthData);
    return response.data;
  }

  async getProfile(): Promise<User> {
    await this.ensureInitialized();
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async updateProfile(profileData: UpdateProfileRequest): Promise<User> {
    await this.ensureInitialized();
    const response = await this.api.put('/auth/profile', profileData);
    return response.data;
  }

  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/change-password', passwordData);
  }

  async forgotPassword(emailData: ForgotPasswordRequest): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/forgot-password', emailData);
  }

  async resetPassword(resetData: ResetPasswordRequest): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/reset-password', resetData);
  }

  async confirmEmail(token: string, email?: string): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/confirm-email', { token, email });
  }

  async resendConfirmationEmail(): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/resend-confirmation');
  }

  async linkOAuthProvider(
    provider: string,
    accessToken: string
  ): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/oauth/link', { provider, accessToken });
  }

  async unlinkOAuthProvider(provider: string): Promise<void> {
    await this.ensureInitialized();
    await this.api.post('/auth/oauth/unlink', { provider });
  }

  async checkEmailExists(email: string) {
    await this.ensureInitialized();
    const response = await this.api.post('/auth/check-email', email);
    return response.data;
  }

  async getUserLocalePreferences(): Promise<{
    preferredCountryCode: string;
    preferredCurrency: string;
    preferredLanguage: string;
    preferredTimezone: string;
  }> {
    await this.ensureInitialized();
    const response = await this.api.get('/auth/locale-preferences');
    return response.data;
  }

  async updateUserLocalePreferences(preferences: {
    preferredCountryCode?: string;
    preferredCurrency?: string;
    preferredLanguage?: string;
    preferredTimezone?: string;
  }): Promise<{ message: string }> {
    await this.ensureInitialized();
    const response = await this.api.put(
      '/auth/locale-preferences',
      preferences
    );
    return response.data;
  }
}

export const authService = new AuthService();
export default authService;
